import axios from 'axios';
import "./RealEstateUpload.css";
import {useCallback, useEffect, useRef, useState} from 'react';
import { createWebSocketClient } from './websocket';
import { v4 as uuidv4 } from 'uuid';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PriceForecastChart from "./PriceForecastChart";
import ClosableCard from "./ClosableCard";
import ApartmentList from "./report/ApartmentList";
import { Accordion } from "react-bootstrap";
import 'bootstrap/dist/css/bootstrap.min.css';
import Metadata from "./report/Metadata";
import Tolerance from "./report/Tolerance";
import ImageAnalyzeList from "./ImageAnalyzeList";


const restClient = axios.create({
    baseURL: process.env.REACT_APP_REST_BACKEND_URL, // Базовый URL
});

const RealEstateUpload = () => {
    const stompClientRef = useRef(null);
    const [isParsing, setIsParsing] = useState(false);
    const [isShowReport, setIsShowReport] = useState(false);
    const [isPredictionDataLoading, setIsPredictionDataLoading] = useState(false);
    const [predictionData, setPredictionData] = useState([]);
    const [marketAnalyzeData, setMarketAnalyzeData] = useState([]);
    const [imageAnalyzeData, setImageAnalyzeData] = useState([]);
    const parsingId = useRef(null);
    const timeout = useRef(null);
    const [images, setImages] = useState([]); // Хранит все изображения
    const [parsingLink, setParsingLink] = useState("");
    const [formData, setFormData] = useState({
        parsingId: "",
        floor: 0,
        floors: 0,
        rooms: 0,
        area: 0,
        address: "",
        latitude: 0.0,
        longitude: 0.0,
        imageCount: 0
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null); // Состояние для выбранного изображения
    const loadingImages = useRef([]);

    async function addLoadingImage(files) {
        const newLoadings = files.map(file => ({
            id: uuidv4(),
            file
        }));

        const newImages = newLoadings.map(image => ({
            id: image.id,
            loading: true,
            url: ""
        }))

        loadingImages.current = newLoadings;
        setImages(prevImages => [...prevImages, ...newImages]);
    }

    const updateLoadingImage = useCallback(async (newIndex, url, imageId) => {
        setImages(prevImages => {
            const updated = [...prevImages]; // создаем копию массива
            updated[newIndex] = {
                ...updated[newIndex], // сохраняем другие поля
                url: url,
                loading: false,
                id: imageId
            };
            return updated;
        });
    }, []);

    function findByField(array, field, value) {
        return array.findIndex(item => item[field] === value);
    }

    const updateLoadingImageByImageId = useCallback(async (imageId, data) => {
        setImages(prevImages => {
            const updated = [...prevImages]; // создаем копию массива
            let imageIndex = findByField(images, "id", imageId);
            updated[imageIndex] = {
                ...imageIndex, // сохраняем другие поля
                url: data.url,
                loading: false,
                id: data.imageId
            };
            return updated;
        });
    }, [images])

    const handleImageUpload = async (event) => {
        const files = Array.from(event.target.files);
        addLoadingImage(files);
    };

    const handleRemoveImage = (index) => {
        setImages(images.filter((_, i) => i !== index));
    };

    const handleImageClick = (img) => {
        setSelectedImage(img.url); // Обновляем изображение для просмотра
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const uploadImages = useCallback(async () =>{
        if (loadingImages.current.length === 0)
            return;
        for (const loading of loadingImages.current) {
            const formData = new FormData();
            formData.append("file", loading.file);

            try {
                restClient.post("/api/image/upload", formData, {
                    headers: {
                        "Content-Type": "multipart/form-data"
                    }
                }).then(response => updateLoadingImageByImageId(loading.id, response.data))
            } catch (error) {
                console.error("Ошибка загрузки", error);
                alert("Ошибка при загрузке файла");
            }
        }
        loadingImages.current = []
    }, [updateLoadingImageByImageId])

    useEffect(() => {
        uploadImages();
    }, [images, uploadImages])

    useEffect(() => {
        if (!isShowReport) {
            setMarketAnalyzeData(null)
            setPredictionData(null)
            setImageAnalyzeData(null)
        }

    }, [isShowReport])

    const handleStartParsingMessage = useCallback((body) => {
        console.log('Start:', body)
        const dto  = JSON.parse(body)
        parsingId.current = dto.parsingId;
    }, [])

    const handleDataParsingMessage = useCallback((body) => {
        const dto  = JSON.parse(body)
        if (parsingId.current !== dto.parsingId)
            return;
        console.log('Data:', body)
        setIsModalOpen(false)
        setFormData(dto);
        setImages(Array.from({ length: dto.imageCount }, () => ({ loading: true, url: "", id: ""})))
    }, [])

    const handleImageDataMessage = useCallback((body) => {
        const dto  = JSON.parse(body)
        if (parsingId.current !== dto.parsingId)
            return;
        console.log('Image:', body)
        updateLoadingImage(dto.order, dto.url)
    }, [updateLoadingImage]);

    const handleFinishMessage = useCallback(async (body) => {
        const dto  = JSON.parse(body)
        if (parsingId.current !== dto.parsingId)
            return;
        console.log('Finish:', body)
        setIsParsing(false);
        parsingId.current = null;
        toast.info("Парсинг завершен")
    }, [])

    const handleErrorMessage = useCallback(async (body) => {
        const dto  = JSON.parse(body)
        if (parsingId.current !== dto.parsingId)
            return;
        console.log('Error:', body)
        setIsParsing(false);
        parsingId.current = null;
        toast.error("Ошибка парсинга: попробуйте снова")
    }, [])

    const handlePredictionResult = useCallback(async (body) => {
        const dto  = JSON.parse(body)
        setIsPredictionDataLoading(false)
        setPredictionData(dto)
        toast.info("Ценовые рекомендации сформированы")
    }, [])

    const handleAnalyzeResult = useCallback(async (body) => {
        const dto  = JSON.parse(body)
        setMarketAnalyzeData(dto)
        toast.info("Анализ рынка завершён")
    }, [])

    const handleImageAnalyzeResult = useCallback(async (body) => {
        const dto  = JSON.parse(body)
        setImageAnalyzeData(dto)
        toast.info("Визуальная оценка квартиры завершена")
    }, [])

    useEffect(() => {
        stompClientRef.current = createWebSocketClient({
            url: process.env.REACT_APP_WS_BACKEND_URL,
            subscriptions: [
                { destination: '/user/queue/parsing/start', callback: msg => handleStartParsingMessage(msg.body) },
                { destination: '/user/queue/parsing/data', callback: msg => handleDataParsingMessage(msg.body) },
                { destination: '/user/queue/parsing/image', callback: msg => handleImageDataMessage(msg.body) },
                { destination: '/user/queue/parsing/finish', callback: msg => handleFinishMessage(msg.body) },
                { destination: '/user/queue/parsing/error', callback: msg => handleErrorMessage(msg.body) },
                { destination: '/user/queue/prediction/result', callback: msg => handlePredictionResult(msg.body) },
                { destination: '/user/queue/prediction/analyze', callback: msg => handleAnalyzeResult(msg.body) },
                { destination: '/user/queue/prediction/image_analyze', callback: msg => handleImageAnalyzeResult(msg.body) },
            ],
            onConnect: () => console.log('Connected!'),
            onError: (frame) => console.log('Connection error:', frame)
        });
        stompClientRef.current.activate();
        return () => {
            if (stompClientRef.current) {
                stompClientRef.current.deactivate();
            }
        };
    }, [handleStartParsingMessage, handleDataParsingMessage, handleErrorMessage,
        handleFinishMessage, handleImageDataMessage, handlePredictionResult, handleAnalyzeResult, handleImageAnalyzeResult]);

    useEffect(() => {
        if (!isParsing) {
            clearTimeout(timeout.current)
            return;
        }
        timeout.current = setTimeout(() => {
            setIsParsing(false);
            parsingId.current = null;
            toast.error("Ошибка парсинга: таймаут превышен")
        }, 30000)
    }, [isParsing])

    async function sendParseMessage() {
        setIsParsing(true)
        if (!stompClientRef.current)
            return;
        stompClientRef.current.publish({
            destination: '/app/api/parsing',
            body: JSON.stringify({ url: parsingLink })
        })
    }

    const sendPredictionRequestMessage = () => {
        console.log("Here")
        setIsPredictionDataLoading(true);
        setIsShowReport(true)
        if (!stompClientRef.current)
            return;
        stompClientRef.current.publish({
            destination: '/app/api/prediction',
            body: JSON.stringify({
                address: formData.address,
                area: formData.area,
                rooms: formData.rooms,
                floor: formData.floor,
                parsingId: formData.parsingId,
                images: images.map(item => item.url)
            })
        })
    }

    if (isShowReport) {
        return (

            <ClosableCard onClose={() => setIsShowReport(false)}>
                <div className="prediction-result-container">
                    <PriceForecastChart
                        data={predictionData}
                        loading={isPredictionDataLoading}
                    />
                </div>

                <div className="image-analyze-container">
                    <Accordion>
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>Визуальный анализ квартиры</Accordion.Header>
                            <Accordion.Body>
                                <ImageAnalyzeList imageAnalyzeData={imageAnalyzeData} />
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </div>

                <div className="apartment-list-container">
                    <Accordion>
                        <Accordion.Item eventKey="0">
                            <Accordion.Header>Анализ рынка</Accordion.Header>
                            <Accordion.Body>
                                <Metadata metadata={marketAnalyzeData?.metadata}/>

                                {/* Карточка с толерантностью */}
                                <Tolerance tolerance={marketAnalyzeData?.tolerance}/>
                                <Accordion>
                                    <Accordion.Item eventKey="0">
                                        <Accordion.Header>Объекты</Accordion.Header>
                                        <Accordion.Body>
                                            <ApartmentList apartments={marketAnalyzeData?.apartments}/>
                                        </Accordion.Body>
                                    </Accordion.Item>
                                </Accordion>
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>
                </div>

                <ToastContainer
                    position="top-right"
                    autoClose={5000}
                    closeOnClick
                    pauseOnHover
                />
            </ClosableCard>
        )
    }

    return (
        <>
            <div className="container">
                <div className="top-bar">
                    <button className="import-btn" onClick={() => setIsModalOpen(true)}>
                        Импортировать
                    </button>
                </div>
                <div className="image-preview">
                    {selectedImage ? (
                        <img src={selectedImage} alt="Selected preview" className="preview-img" />
                    ) : (
                        <span className="placeholder-text">No image selected</span>
                    )}
                </div>

                <div className="image-list">
                    {images.map((img, index) => (
                        <div key={index} className="thumbnail-wrapper">
                            {img.loading ? (
                                <div className="loader"></div>
                            ) : (
                                <img
                                    src={img.url}
                                    alt="Thumbnail"
                                    className="thumbnail"
                                    onClick={() => handleImageClick(img)} // Щелчок на миниатюре
                                />
                            )}
                            <button
                                onClick={() => handleRemoveImage(index)}
                                className="remove-btn"
                            >
                                X
                            </button>
                        </div>
                    ))}
                    <input
                        type="file"
                        multiple
                        onChange={handleImageUpload}
                        className="hidden"
                        id="fileUpload"
                        disabled={isParsing}
                    />
                    <label htmlFor="fileUpload" className="upload-btn">
                        +
                    </label>
                </div>

                <div className="form">
                    <div className="form-group">
                        <label htmlFor="address">Адрес</label>
                        <input
                            id="address"
                            name="address"
                            placeholder="Введите адрес"
                            value={formData.address || ""}
                            onChange={handleChange}
                            className="input"
                            disabled={isParsing}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="area">Площадь (м²)</label>
                        <input
                            id="area"
                            name="area"
                            placeholder="Укажите площадь"
                            value={formData.area || ""}
                            onChange={handleChange}
                            className="input"
                            disabled={isParsing}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="floor">Этаж квартиры</label>
                        <input
                            id="floor"
                            name="floor"
                            placeholder="Например, 3"
                            value={formData.floor || ""}
                            onChange={handleChange}
                            className="input"
                            disabled={isParsing}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="floors">Всего этажей в доме</label>
                        <input
                            id="floors"
                            name="floors"
                            placeholder="Например, 9"
                            value={formData.floors || ""}
                            onChange={handleChange}
                            className="input"
                            disabled={isParsing}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="rooms">Количество комнат</label>
                        <input
                            id="rooms"
                            name="rooms"
                            placeholder="Например, 2"
                            value={formData.rooms || ""}
                            onChange={handleChange}
                            className="input"
                            disabled={isParsing}
                        />
                    </div>
                </div>
                <button className="predict-btn" onClick={sendPredictionRequestMessage} disabled={isParsing}>Predict Price</button>
            </div>
            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="import-modal">
                        <h3>Импорт с сайта Суточно.ру</h3>
                        <input
                            type="text"
                            name="link"
                            placeholder="Вставьте ссылку"
                            value={parsingLink}
                            onChange={(e) => setParsingLink(e.target.value)}
                            className="input"
                        />
                        <div className="modal-buttons">
                            <button className="fetch-btn" onClick={sendParseMessage} disabled={isParsing}>
                                {isParsing ? (
                                    <span className="spinner" />
                                ) : (
                                    "Загрузить"
                                )}
                            </button>
                            <button onClick={() => setIsModalOpen(false)} className="cancel-btn">
                                Закрыть
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <ToastContainer
                position="top-right"
                autoClose={5000}
                closeOnClick
                pauseOnHover
            />
        </>

    );
};

export default RealEstateUpload;
